/**
 * The composer's microphone (R57): its word and shortcut on hover and focus, the reason it refuses
 * when Buddy cannot work (same reason as sending, still focusable), the crossed glyph when the
 * browser blocks the microphone, and a ✓ for a moment after the text landed.
 */

import { Tooltip } from '@base-ui/react/tooltip';
import clsx from 'clsx';
import { useId, type PointerEvent, type Ref } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckGlyph } from '~/shared/ui/FormGlyphs';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { MicGlyph, MicOffGlyph } from './ChatGlyphs';
import styles from './Composer.module.css';

export interface DictationMicProps {
  /** Why it refuses, already worded; `null` while it works. */
  readonly refusal: string | null;
  readonly denied: boolean;
  /** The text just landed: the glyph answers with a ✓. */
  readonly acknowledged: boolean;
  /** Under the recording lane: hidden and out of the tab order. */
  readonly under: boolean;
  readonly className?: string;
  readonly micRef: Ref<HTMLButtonElement>;
  readonly onPointerDown: (event: PointerEvent<HTMLButtonElement>) => void;
  readonly onStart: () => void;
}

/** ⌃M on a Mac, where Ctrl is the ⌃ key; Ctrl M everywhere else. */
const onMac = typeof navigator !== 'undefined' && /Mac/.test(navigator.userAgent);

export function DictationMic({ refusal, denied, acknowledged, under, className, micRef, onPointerDown, onStart }: DictationMicProps) {
  const { t } = useTranslation();
  const reasonId = useId();
  const off = refusal !== null;
  return (
    <>
      <Tooltip.Root>
        <Tooltip.Trigger
          render={
            <button
              ref={micRef}
              type="button"
              className={clsx(styles.icon, off ? styles.iconDim : styles.iconLive, under && styles.under, className)}
              aria-label={denied ? t('web:aiPlan.dictation.labelDenied') : t('web:aiPlan.composer.dictation')}
              aria-keyshortcuts="Control+M"
              aria-disabled={off ? 'true' : undefined}
              aria-describedby={off ? reasonId : undefined}
              inert={under}
              onPointerDown={onPointerDown}
              onClick={off ? undefined : onStart}
            />
          }
        >
          {denied ? (
            <MicOffGlyph className={styles.micGlyph} />
          ) : (
            <>
              <MicGlyph className={clsx(styles.micGlyph, acknowledged && styles.micGlyphOut)} />
              <CheckGlyph className={clsx(styles.micGlyph, !acknowledged && styles.micGlyphOut)} />
            </>
          )}
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Positioner className={styles.tipPositioner} sideOffset={10}>
            {/* `aria-hidden`: the button's name and `aria-describedby` already say all of this. */}
            <Tooltip.Popup className={styles.tip} aria-hidden="true">
              {off ? (
                refusal
              ) : (
                <>
                  {t('web:aiPlan.dictation.tip')}
                  {denied ? (
                    <span className={styles.tipSub}>{t('web:aiPlan.dictation.tipDenied')}</span>
                  ) : (
                    <kbd className={styles.tipKeys}>{onMac ? t('web:aiPlan.dictation.tipKeysMac') : t('web:aiPlan.dictation.tipKeys')}</kbd>
                  )}
                </>
              )}
            </Tooltip.Popup>
          </Tooltip.Positioner>
        </Tooltip.Portal>
      </Tooltip.Root>
      {off && (
        <span id={reasonId} className={hidden.srOnly}>
          {refusal}
        </span>
      )}
    </>
  );
}
